import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Flag, Heart, Link2, Loader2, MessageSquare, Trash2 } from "lucide-react";
import DebateThreadView from "./DebateThreadView";
import VerdictMeter from "./VerdictMeter";
import Dialog from "./Dialog";
import { expertOptions } from "../constants/experts";
import { useAuth } from "../contexts/AuthContext";
import { hasFreeAccess } from "../utils/freeAccess";
import {
  ContentWarning,
  deleteFeedComment,
  FeedComment,
  FeedContent,
  feedErrorMessage,
  FeedPost as FeedPostData,
  hasLiked,
  loadFeedComments,
  loadFeedContent,
  moderateFeedItem,
  postFeedComment,
  reportFeedItem,
  ReportReason,
  setLiked,
} from "../firebase/feed";

const MAX_COMMENT_LENGTH = 1000;

export function expertLabel(key: string): string {
  return (
    expertOptions.find((e) => e.value === key)?.label ??
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function relativeTime(ms: number | null, now: number): string {
  if (ms == null) return "just now";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const WARNING_TEXT: Record<Exclude<ContentWarning, null>, string> = {
  language: "This contains strong language.",
  sensitive: "This contains content some people may find upsetting.",
};

/**
 * Blurs content flagged at posting time until the reader says they want to
 * see it. The reveal lasts for this visit only.
 */
export function WarningGate({
  warning,
  revealed,
  onReveal,
  children,
  compact = false,
}: {
  warning: ContentWarning;
  revealed: boolean;
  onReveal: () => void;
  children: React.ReactNode;
  compact?: boolean;
}) {
  if (!warning || revealed) return <>{children}</>;
  return (
    <div className="relative">
      <div aria-hidden="true" className="select-none blur-sm pointer-events-none" style={{ maxHeight: compact ? 44 : 120, overflow: "hidden" }}>
        {children}
      </div>
      <div className={`absolute inset-0 flex ${compact ? "items-center gap-3" : "flex-col items-start justify-center gap-2"} bg-canvas/80 px-1`}>
        <p className="text-sm text-content">{WARNING_TEXT[warning]}</p>
        <button
          type="button"
          onClick={onReveal}
          className="px-3 py-1.5 rounded-md border border-line bg-surface text-sm font-medium text-content hover:bg-subtle active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus transition-colors duration-150"
        >
          Show anyway
        </button>
      </div>
    </div>
  );
}

const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "offensive", label: "Offensive or hateful" },
  { value: "spam", label: "Spam or advertising" },
  { value: "misleading", label: "Dangerous or misleading" },
  { value: "other", label: "Something else" },
];

function ReportDialog({
  open,
  onClose,
  target,
}: {
  open: boolean;
  onClose: () => void;
  target: { postId: string; commentId?: string };
}) {
  const { currentUser } = useAuth();
  const [reason, setReason] = useState<ReportReason>("offensive");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "duplicate" | "error">("idle");
  useEffect(() => {
    if (open) setStatus("idle");
  }, [open]);

  const send = async () => {
    if (!currentUser) return;
    setStatus("sending");
    try {
      setStatus((await reportFeedItem(target, currentUser.uid, reason)) ? "sent" : "duplicate");
    } catch {
      setStatus("error");
    }
  };

  const title = target.commentId ? "Report comment" : "Report debate";
  if (status === "sent" || status === "duplicate") {
    return (
      <Dialog open={open} onClose={onClose} title={title}>
        <p className="text-sm text-muted leading-6">
          {status === "sent"
            ? "Thanks. Posts that several people report are hidden until they're reviewed."
            : "You've already reported this."}
        </p>
        <button type="button" className="button-secondary w-full" onClick={onClose}>Close</button>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <fieldset className="space-y-1">
        <legend className="text-sm text-muted mb-2">What's wrong with it?</legend>
        {REPORT_REASONS.map((r) => (
          <label key={r.value} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-content hover:bg-subtle cursor-pointer">
            <input
              type="radio"
              name="report-reason"
              value={r.value}
              checked={reason === r.value}
              onChange={() => setReason(r.value)}
              className="accent-current focus-visible:ring-2 focus-visible:ring-focus"
            />
            {r.label}
          </label>
        ))}
      </fieldset>
      {status === "error" && <p role="alert" className="text-sm text-danger">Couldn't send the report. Try again.</p>}
      <button type="button" className="button-primary w-full" disabled={status === "sending"} onClick={send}>
        {status === "sending" && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
        Send report
      </button>
    </Dialog>
  );
}

const actionClass =
  "inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm text-muted hover:bg-subtle hover:text-content active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150";

function Comments({
  postId,
  onCountChange,
  now,
}: {
  postId: string;
  onCountChange: (delta: number) => void;
  now: number;
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState<FeedComment[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const [reporting, setReporting] = useState<string | null>(null);

  const reload = () =>
    loadFeedComments(postId)
      .then((c) => {
        setComments(c);
        setLoadError(false);
      })
      .catch(() => setLoadError(true));

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await postFeedComment(postId, text);
      setDraft("");
      onCountChange(1);
      await reload();
    } catch (err) {
      setSendError(feedErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id: string) => {
    setComments((c) => c?.filter((x) => x.id !== id) ?? c);
    onCountChange(-1);
    try {
      await deleteFeedComment(postId, id);
    } catch {
      onCountChange(1);
      reload();
    }
  };

  const removeAsModerator = async (id: string) => {
    if (!window.confirm("Remove this comment permanently?")) return;
    try {
      await moderateFeedItem({ postId, commentId: id }, "remove");
      setComments((c) => c?.filter((x) => x.id !== id) ?? c);
      onCountChange(-1);
    } catch (err) {
      setSendError(feedErrorMessage(err));
    }
  };

  return (
    <section aria-label="Comments" className="mt-6 pt-5 border-t border-line">
      <h3 className="text-sm font-semibold text-content">
        Comments {comments && <span className="font-mono tabular-nums text-muted">{comments.length}</span>}
      </h3>
      {loadError && <p className="mt-3 text-sm text-danger">Couldn't load comments.</p>}
      {!comments && !loadError && <p className="mt-3 text-sm text-muted">Loading comments…</p>}
      {comments && comments.length === 0 && <p className="mt-3 text-sm text-muted">No comments yet.</p>}
      {comments && comments.length > 0 && (
        <ul className="mt-2 divide-y divide-line">
          {comments.map((c) => (
            <li key={c.id} className="py-3">
              <div className="flex items-center gap-2 text-xs text-muted">
                <span className="font-medium text-content">{c.authorName}</span>
                <span className="font-mono tabular-nums">{relativeTime(c.createdAt?.toMillis() ?? null, now)}</span>
                <span className="ml-auto flex items-center gap-0.5">
                  {currentUser?.uid !== c.authorUid && hasFreeAccess(currentUser?.email) && (
                    <button type="button" className={actionClass} aria-label="Remove comment as moderator" title="Remove comment" onClick={() => removeAsModerator(c.id)}>
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                  {currentUser?.uid === c.authorUid ? (
                    <button type="button" className={actionClass} aria-label="Delete comment" title="Delete comment" onClick={() => remove(c.id)}>
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  ) : currentUser ? (
                    <button type="button" className={actionClass} aria-label="Report comment" title="Report comment" onClick={() => setReporting(c.id)}>
                      <Flag size={14} aria-hidden="true" />
                    </button>
                  ) : null}
                </span>
              </div>
              <WarningGate
                compact
                warning={c.contentWarning}
                revealed={revealed.has(c.id)}
                onReveal={() => setRevealed((s) => new Set(s).add(c.id))}
              >
                <p className="mt-1 text-sm text-content leading-6 whitespace-pre-wrap break-words">{c.text}</p>
              </WarningGate>
            </li>
          ))}
        </ul>
      )}

      {currentUser ? (
        <div className="mt-4">
          <label htmlFor={`comment-${postId}`} className="sr-only">Add a comment</label>
          <textarea
            id={`comment-${postId}`}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value.slice(0, MAX_COMMENT_LENGTH));
              setSendError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
            }}
            rows={3}
            placeholder="Add a comment"
            className="w-full px-3 py-2 rounded-md border border-line bg-surface text-sm text-content placeholder:text-muted hover:border-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 resize-y"
            disabled={sending}
          />
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xs text-muted font-mono tabular-nums">{draft.length} / {MAX_COMMENT_LENGTH}</span>
            {sendError && <span role="alert" className="text-xs text-danger">{sendError}</span>}
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim() || sending}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-action text-on-solid text-sm font-semibold hover:bg-action-hover active:translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-[background-color,transform] duration-150"
            >
              {sending && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
              {sending ? "Posting…" : "Post comment"}
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-muted">
          <button type="button" className="font-medium text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm" onClick={() => navigate("/login")}>
            Sign in
          </button>{" "}
          to comment.
        </p>
      )}
      {reporting && (
        <ReportDialog open onClose={() => setReporting(null)} target={{ postId, commentId: reporting }} />
      )}
    </section>
  );
}

export default function FeedPost({
  post,
  onRemoved,
  expanded,
  onToggle,
  revealed,
  onReveal,
  now,
}: {
  post: FeedPostData;
  onRemoved: () => void;
  expanded: boolean;
  onToggle: () => void;
  revealed: boolean;
  onReveal: () => void;
  now: number;
}) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [liked, setLikedState] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likeCount);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [liking, setLiking] = useState(false);
  const [content, setContent] = useState<FeedContent | null>(null);
  const [contentState, setContentState] = useState<"idle" | "loading" | "error" | "missing">("idle");
  const [reporting, setReporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const isModerator = hasFreeAccess(currentUser?.email);

  const removeAsModerator = async () => {
    if (!window.confirm("Remove this debate from the feed permanently?")) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await moderateFeedItem({ postId: post.id }, "remove");
      onRemoved();
    } catch (err) {
      setRemoveError(feedErrorMessage(err));
      setRemoving(false);
    }
  };

  useEffect(() => {
    setLikeCount(post.likeCount);
    setCommentCount(post.commentCount);
  }, [post.likeCount, post.commentCount]);

  useEffect(() => {
    if (!currentUser) {
      setLikedState(false);
      return;
    }
    let live = true;
    hasLiked(post.id, currentUser.uid).then((v) => live && setLikedState(v)).catch(() => {});
    return () => {
      live = false;
    };
  }, [post.id, currentUser]);

  const contentNeeded = expanded && (!post.contentWarning || revealed);
  useEffect(() => {
    if (!contentNeeded || content || contentState === "loading") return;
    setContentState("loading");
    loadFeedContent(post.id)
      .then((c) => {
        setContent(c);
        setContentState(c ? "idle" : "missing");
      })
      .catch(() => setContentState("error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentNeeded, post.id]);

  const toggleLike = async () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }
    if (liking) return;
    const next = !liked;
    setLiking(true);
    setLikedState(next);
    setLikeCount((n) => n + (next ? 1 : -1));
    try {
      await setLiked(post.id, currentUser.uid, next);
    } catch {
      setLikedState(!next);
      setLikeCount((n) => n + (next ? -1 : 1));
    } finally {
      setLiking(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/feed/${post.id}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      navigate(`/feed/${post.id}`);
    }
  };

  const panel = post.debateMode === "single" ? "Deep analysis" : post.experts.map(expertLabel).join(", ");
  const blurred = !!post.contentWarning && !revealed;

  return (
    <article className="py-5 border-b border-line" aria-labelledby={`post-${post.id}`}>
      <div className="flex items-center gap-2 text-xs text-muted min-w-0">
        <span className="font-medium text-content truncate max-w-[40%]">{post.authorName}</span>
        <span aria-hidden="true">·</span>
        <time className="font-mono tabular-nums flex-shrink-0" dateTime={post.publishedAt ? new Date(post.publishedAt.toMillis()).toISOString() : undefined}>
          {relativeTime(post.publishedAt?.toMillis() ?? null, now)}
        </time>
        <span aria-hidden="true">·</span>
        <span className="truncate">{panel}</span>
      </div>

      <WarningGate warning={post.contentWarning} revealed={revealed} onReveal={onReveal}>
        <h2 id={`post-${post.id}`} className="mt-1.5 text-base font-semibold text-content leading-snug break-words">
          <Link to={`/feed/${post.id}`} className="hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus rounded-sm">
            {post.question}
          </Link>
        </h2>
        {!expanded && post.verdict && (
          <div className="mt-3 max-w-md">
            <VerdictMeter verdict={post.verdict} compact />
          </div>
        )}
        {!expanded && post.verdictExcerpt && (
          <p className="mt-1.5 text-sm text-muted leading-6 line-clamp-3">{post.verdictExcerpt}</p>
        )}
      </WarningGate>

      <div className="mt-2 -ml-2 flex items-center gap-0.5 flex-wrap">
        <button
          type="button"
          onClick={toggleLike}
          disabled={liking}
          aria-pressed={liked}
          aria-label={liked ? `Unlike, ${likeCount} likes` : `Like, ${likeCount} likes`}
          className={`${actionClass} ${liked ? "text-danger hover:text-danger" : ""}`}
        >
          <Heart size={15} aria-hidden="true" fill={liked ? "currentColor" : "none"} />
          <span className="font-mono tabular-nums">{likeCount}</span>
        </button>
        <button type="button" onClick={onToggle} className={actionClass} aria-label={`${commentCount} comments`}>
          <MessageSquare size={15} aria-hidden="true" />
          <span className="font-mono tabular-nums">{commentCount}</span>
        </button>
        <button type="button" onClick={onToggle} aria-expanded={expanded} className={actionClass}>
          {expanded ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronDown size={15} aria-hidden="true" />}
          {expanded ? "Collapse" : "Read debate"}
        </button>
        <span className="ml-auto flex items-center gap-0.5">
          <button type="button" onClick={copyLink} className={actionClass} aria-label="Copy link" title="Copy link">
            <Link2 size={15} aria-hidden="true" />
            {copied && <span className="text-xs">Copied</span>}
          </button>
          {isModerator && (
            <button type="button" onClick={removeAsModerator} disabled={removing} className={actionClass} aria-label="Remove debate as moderator" title="Remove debate">
              {removing ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Trash2 size={15} aria-hidden="true" />}
            </button>
          )}
          {currentUser && (
            <button type="button" onClick={() => setReporting(true)} className={actionClass} aria-label="Report debate" title="Report debate">
              <Flag size={15} aria-hidden="true" />
            </button>
          )}
        </span>
      </div>

      {removeError && <p role="alert" className="mt-1 text-xs text-danger">{removeError}</p>}
      {expanded && !blurred && (
        <div className="mt-4 feed-thread">
          {contentState === "loading" && <p className="text-sm text-muted" role="status">Loading debate…</p>}
          {contentState === "error" && <p className="text-sm text-danger">Couldn't load this debate.</p>}
          {contentState === "missing" && <p className="text-sm text-muted">This debate is no longer available.</p>}
          {content && (
            <DebateThreadView
              debateTopic={post.question}
              councilProgress={content.steps}
              chaosProgress={content.chaosSteps}
              councilFinal={content.finalAggregate}
              facilitatorDone
              isLoading={false}
              selectedExperts={post.experts}
              debateMode={post.debateMode}
              askerName={post.authorName}
              verdict={post.verdict}
              sources={content.sources}
            />
          )}
          <Comments postId={post.id} now={now} onCountChange={(d) => setCommentCount((n) => Math.max(0, n + d))} />
        </div>
      )}
      {expanded && blurred && (
        <p className="mt-3 text-sm text-muted">Choose “Show anyway” above to read this debate.</p>
      )}
      {reporting && <ReportDialog open onClose={() => setReporting(false)} target={{ postId: post.id }} />}
    </article>
  );
}
